/**
 * Cart assistant: auto-add/remove package protection lines to match eligible physical items.
 * Enforcement is handled by the shipping-protection Shopify validation function at checkout.
 */
(function () {
  const TIER_NONE = "none";
  const TIER_STANDARD = "standard";
  const TIER_PREMIUM = "premium";

  let syncTimer = null;
  let syncInFlight = false;
  let syncQueued = false;

  function getConfig() {
    const cfg = window.shippingProtection;
    if (!cfg || !cfg.enabled) return null;
    const standardVariantId = normalizeVariantId(cfg.standardVariantId);
    const premiumVariantId = normalizeVariantId(cfg.premiumVariantId);
    if (!standardVariantId || !premiumVariantId) return null;
    return { standardVariantId, premiumVariantId, policyUrl: cfg.policyUrl || "" };
  }

  function normalizeVariantId(id) {
    if (id == null || id === "") return null;
    const s = String(id).trim();
    const match = s.match(/(\d+)\s*$/);
    return match ? match[1] : s.replace(/\D/g, "") || s;
  }

  function variantIdFromLine(line) {
    if (!line || line.variant_id == null) return null;
    return normalizeVariantId(line.variant_id);
  }

  function isProtectionVariant(variantId, config) {
    return (
      variantId === config.standardVariantId ||
      variantId === config.premiumVariantId
    );
  }

  function parseMetafieldBool(value) {
    if (value == null || value === "") return null;
    const v = String(value).trim().toLowerCase();
    if (v === "false" || v === "0" || v === "no") return false;
    if (v === "true" || v === "1" || v === "yes") return true;
    return null;
  }

  function lineTierFromProperties(line) {
    const props = line.properties || {};
    const tier = props._shipping_protection_tier || props.shipping_protection_tier;
    if (tier != null && String(tier).trim() !== "") {
      return String(tier).trim().toLowerCase();
    }
    return null;
  }

  /**
   * @param {object} cart - /cart.js payload
   * @param {object} config
   * @returns {'none'|'standard'|'premium'}
   */
  function computeRequiredTier(cart, config, tierMap) {
    if (!cart || !Array.isArray(cart.items)) return TIER_NONE;

    let required = TIER_NONE;
    const map = tierMap || {};

    for (const line of cart.items) {
      const variantId = variantIdFromLine(line);
      if (!variantId || isProtectionVariant(variantId, config)) continue;

      const requiresShipping = line.requires_shipping !== false;
      if (!requiresShipping) continue;

      if (!requiresProtectionForLine(line, map)) continue;

      let tier = tierForLine(line, map);
      if (tier == null || tier === "") tier = TIER_STANDARD;
      if (tier === TIER_NONE) continue;

      const lineTier = tier === TIER_PREMIUM ? TIER_PREMIUM : TIER_STANDARD;
      if (lineTier === TIER_PREMIUM) required = TIER_PREMIUM;
      else if (required !== TIER_PREMIUM) required = TIER_STANDARD;
    }

    return required;
  }

  function countProtectionLines(cart, config) {
    let standardQty = 0;
    let premiumQty = 0;
    const linesToChange = [];

    cart.items.forEach((line, index) => {
      const variantId = variantIdFromLine(line);
      if (variantId === config.standardVariantId) {
        standardQty += line.quantity || 0;
        linesToChange.push({ line: index + 1, variantId, kind: "standard" });
      } else if (variantId === config.premiumVariantId) {
        premiumQty += line.quantity || 0;
        linesToChange.push({ line: index + 1, variantId, kind: "premium" });
      }
    });

    return { standardQty, premiumQty, linesToChange };
  }

  function getRoutes() {
    const r = typeof routes !== "undefined" ? routes : {};
    const root =
      typeof Shopify !== "undefined" &&
      Shopify.routes &&
      Shopify.routes.root != null
        ? String(Shopify.routes.root).replace(/\/?$/, "")
        : "";
    const base = root ? `${root}/` : "/";
    return {
      cartAdd: r.cart_add_url || `${base}cart/add.js`,
      cartChange: r.cart_change_url || `${base}cart/change.js`,
      cartJs: `${base}cart.js`.replace(/\/{2,}/g, "/"),
    };
  }

  function cartFetchConfig() {
    return typeof window.fetchConfig === "function"
      ? window.fetchConfig()
      : { method: "POST", headers: { "Content-Type": "application/json" } };
  }

  let tierCache = null;
  let tierCacheAt = 0;
  const TIER_CACHE_MS = 5000;

  async function loadTierMap() {
    const now = Date.now();
    if (tierCache && now - tierCacheAt < TIER_CACHE_MS) return tierCache;

    const { cartJs } = getRoutes();
    const base = cartJs.replace(/\.js\/?$/, "");
    const url = `${base}?view=shipping-protection-data`;

    try {
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return {};
      const data = await res.json();
      tierCache = data.tiers || {};
      tierCacheAt = now;
      return tierCache;
    } catch (_) {
      return {};
    }
  }

  function tierForLine(line, tierMap) {
    const fromProps = lineTierFromProperties(line);
    if (fromProps) return fromProps;

    const variantId = variantIdFromLine(line);
    if (variantId && tierMap && tierMap[variantId]) {
      const entry = tierMap[variantId];
      if (entry && entry.tier) return String(entry.tier).toLowerCase();
    }
    return null;
  }

  function requiresProtectionForLine(line, tierMap) {
    const variantId = variantIdFromLine(line);
    if (variantId && tierMap && tierMap[variantId]) {
      const entry = tierMap[variantId];
      if (entry && entry.requiresShippingProtection === false) return false;
    }
    const optOut = parseMetafieldBool(line.requires_shipping_protection);
    if (optOut === false) return false;
    return true;
  }

  async function fetchCart() {
    const { cartJs } = getRoutes();
    const res = await fetch(cartJs, { credentials: "same-origin" });
    if (!res.ok) throw new Error("cart fetch failed");
    return res.json();
  }

  async function changeLine(line, quantity) {
    const { cartChange } = getRoutes();
    const body = JSON.stringify({ line, quantity });
    const res = await fetch(cartChange, {
      ...cartFetchConfig(),
      method: "POST",
      body,
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error("cart change failed");
    return res.json();
  }

  async function addProtection(variantId, tier) {
    const { cartAdd } = getRoutes();
    const body = JSON.stringify({
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: {
            _package_protection: "true",
            _shipping_protection_tier: tier,
          },
        },
      ],
    });
    const res = await fetch(cartAdd, {
      ...cartFetchConfig(),
      method: "POST",
      body,
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error("cart add failed");
    return res.json();
  }

  /**
   * @param {object} [cart] - optional existing cart payload
   */
  async function syncProtection(cart) {
    const config = getConfig();
    if (!config) return cart || null;

    if (syncInFlight) {
      syncQueued = true;
      return cart || null;
    }

    syncInFlight = true;
    try {
      let current = cart;
      if (!current) current = await fetchCart();

      tierCache = null;
      const tierMap = await loadTierMap();
      const requiredTier = computeRequiredTier(current, config, tierMap);
      const { standardQty, premiumQty, linesToChange } = countProtectionLines(
        current,
        config,
      );

      const wrongLines = linesToChange.filter((entry) => {
        if (requiredTier === TIER_NONE) return true;
        if (requiredTier === TIER_STANDARD) return entry.kind !== "standard";
        return entry.kind !== "premium";
      });

      for (let i = wrongLines.length - 1; i >= 0; i--) {
        await changeLine(wrongLines[i].line, 0);
        current = await fetchCart();
      }

      const afterRemove = countProtectionLines(current, config);
      let stdQty = afterRemove.standardQty;
      let premQty = afterRemove.premiumQty;

      if (requiredTier === TIER_NONE) {
        return current;
      }

      const targetId =
        requiredTier === TIER_PREMIUM
          ? config.premiumVariantId
          : config.standardVariantId;

      if (requiredTier === TIER_STANDARD) {
        if (premQty > 0) {
          for (const entry of [...afterRemove.linesToChange].reverse()) {
            if (entry.kind === "premium") await changeLine(entry.line, 0);
          }
          current = await fetchCart();
          const recount = countProtectionLines(current, config);
          stdQty = recount.standardQty;
          premQty = recount.premiumQty;
        }
        if (stdQty === 0) {
          await addProtection(config.standardVariantId, TIER_STANDARD);
          current = await fetchCart();
        } else if (stdQty > 1) {
          const stdLine = afterRemove.linesToChange.find(
            (e) => e.kind === "standard",
          );
          if (stdLine) await changeLine(stdLine.line, 1);
          current = await fetchCart();
        }
      } else if (requiredTier === TIER_PREMIUM) {
        if (stdQty > 0) {
          for (const entry of [...afterRemove.linesToChange].reverse()) {
            if (entry.kind === "standard") await changeLine(entry.line, 0);
          }
          current = await fetchCart();
          const recount = countProtectionLines(current, config);
          stdQty = recount.standardQty;
          premQty = recount.premiumQty;
        }
        if (premQty === 0) {
          await addProtection(config.premiumVariantId, TIER_PREMIUM);
          current = await fetchCart();
        } else if (premQty > 1) {
          const premLine = countProtectionLines(current, config).linesToChange.find(
            (e) => e.kind === "premium",
          );
          if (premLine) await changeLine(premLine.line, 1);
          current = await fetchCart();
        }
      }

      return current;
    } finally {
      syncInFlight = false;
      if (syncQueued) {
        syncQueued = false;
        scheduleSync();
      }
    }
  }

  function scheduleSync(cart) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      syncProtection(cart).then((updated) => {
        if (!updated) return;
        try {
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:protection-synced", {
              bubbles: true,
              detail: { cart: updated },
            }),
          );
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:updated", {
              bubbles: true,
              detail: { cart: updated },
            }),
          );
        } catch (_) {}
      });
    }, 300);
  }

  function onCartUpdated(event) {
    if (syncInFlight) return;
    const cart = event && event.detail && event.detail.cart;
    scheduleSync(cart);
  }

  document.addEventListener("cart:updated", onCartUpdated);
  document.addEventListener("cart:refresh", onCartUpdated);

  window.shippingProtectionSync = {
    computeRequiredTier,
    syncProtection,
    scheduleSync,
    getConfig,
  };
})();
